// MEMBRO EXTERNO DO CASO — painel de votação (operador, 2026-08-18).
//
// Três estados, e o painel não renderiza nada fora deles (rota `padrao` não tem membro
// externo, e um painel vazio no topo do caso seria ruído em todo caso do sistema):
//
//   1. INDICAÇÃO ABERTA  → quem decide vê "Aprovo / Não aprovo"; quem não decide vê quem
//                          falta. É o estado que TRAVA o caso, e por isso ele fica no
//                          topo: enterrá-lo abaixo do quórum faria a pessoa procurar por
//                          que o rito não anda.
//   2. APROVADO          → o externo participa como membro do comitê.
//   3. INDICAÇÃO CAIU    → uma reprovação derrubou o nome; é preciso indicar outro pela
//                          triagem.
//
// ⚠️ O painel NÃO recalcula quem pode votar. `euDecido` vem do servidor pelo mesmo motivo
// que `conduzUnipessoal`: a regra é o roster exigível do caso, e uma segunda redação aqui
// divergiria justamente no caso com membro denunciado — que é onde esta feature é usada.
// O botão some porque o servidor disse que esta pessoa não decide, nunca porque a tela
// achou isso sozinha.
//
// ⚠️ E o botão é conveniência, não defesa: o gate real é o 403 `nao_decide_neste_caso` da
// rota. Esconder na tela sem fechar no servidor é o "defesa na camada de APRESENTAÇÃO" que
// o CLAUDE.md registra — um POST direto passaria por cima.
//
// ## Quem JÁ VOTOU não vê mais os botões (relato do operador, 2026-08-19)
//
// Os dois botões continuavam na tela depois do voto, porque `euDecido` responde "esta
// pessoa DECIDE neste caso?" e não "esta pessoa JÁ decidiu?" — são perguntas diferentes e
// a primeira segue verdadeira para sempre. O sintoma era um convite a votar de novo num
// ato que a trilha já registrou (o servidor absorve: o fold `membroExternoDoCaso` guarda
// `aprovaram` num Set, então o segundo "Aprovo" é idempotente — mas a tela mentia sobre
// haver algo pendente do leitor).
//
// 🔴 O "já votei" NÃO é predicado novo: é `faltam`, que o servidor já manda e que a própria
// tela já renderiza logo acima. `faltam` é literalmente "elegíveis que ainda não votaram
// nesta indicação" — quem decide e não está nele, votou. Recalcular a elegibilidade aqui
// seria a segunda redação que o ⚠️ acima proíbe; consultar a lista que o servidor mandou
// não é.
//
// `meuId` ausente (testes que montam sem sessão) mantém o comportamento anterior: com
// `euDecido` os botões aparecem. Degradar para "esconder" faria a tela sonegar a ação por
// falta de informação sobre QUEM está lendo, que é o pior desfecho dos dois.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card } from '@aions/ui-primitives';
import { apiFetch } from '../lib/auth-client';

export interface MembroExternoInfo {
  indicadoId: string | null;
  aprovadoId: string | null;
  aprovaram: ReadonlyArray<string>;
  faltam: ReadonlyArray<string>;
  ultimaReprovada: { externoId: string; porId: string } | null;
  euDecido: boolean;
  publicKey: { memberId: string; publicKeyJwk: string } | null;
}

export function MembroExternoPanel({
  caseId,
  externo,
  rotuloDe,
  meuId,
  onChanged,
}: {
  caseId: string;
  externo: MembroExternoInfo | null;
  /** id → e-mail, resolvido pelo `/members` do chamador. Devolve o ID CRU quando não
   *  conhece o nome — nunca omite a linha, senão "faltam 2" vira "1 exibido". */
  rotuloDe: (id: string) => string;
  /** Quem está lendo. Vem de cima e não de um `useSession()` aqui dentro — mesmo desenho
   *  do `PendentesLista`, que os testes montam sem provider. */
  meuId?: string;
  onChanged: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [enviando, setEnviando] = useState<'sim' | 'nao' | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  // Nada indicado e nada aprovado → o caso não passa por aqui. Também cobre a rota
  // `padrao`, onde `membroExterno` vem com tudo nulo.
  if (externo === null) return null;
  const semHistorico =
    externo.indicadoId === null && externo.aprovadoId === null && externo.ultimaReprovada === null;
  if (semHistorico) return null;

  /**
   * Este leitor já votou na indicação vigente? Ver o 🔴 do cabeçalho: derivado de
   * `faltam`, a resposta que o servidor já deu, nunca de um recálculo de elegibilidade.
   *
   * 🔴 O `euDecido &&` NÃO É REDUNDANTE, e omiti-lo foi um bug real (pego pelo teste
   * "quem NÃO decide não vê botão NEM a copy de voto registrado"): `faltam` lista os
   * ELEGÍVEIS que não votaram, então quem nunca foi elegível também está fora dela — e
   * "fora de faltam" sozinho lia isso como voto dado. A tela passava a afirmar a quem
   * não vota que o voto dela foi registrado.
   */
  const jaVotei =
    externo.euDecido &&
    typeof meuId === 'string' &&
    meuId.length > 0 &&
    !externo.faltam.includes(meuId);

  const votar = async (aprovou: boolean): Promise<void> => {
    setEnviando(aprovou ? 'sim' : 'nao');
    setErro(null);
    try {
      const resp = await apiFetch(`/api/comite/cases/${caseId}/membro-externo/voto`, {
        method: 'POST',
        body: JSON.stringify({ aprovou }),
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { error?: string };
        // Guarda o CÓDIGO, traduz na renderização — padrão das outras telas.
        setErro(body.error ?? 'error_unknown');
        return;
      }
      onChanged();
    } finally {
      setEnviando(null);
    }
  };

  return (
    <Card data-testid="membro-externo-panel">
      <h3 style={{ marginTop: 0 }}>{t('triagem.externo.titulo_painel')}</h3>

      {externo.aprovadoId !== null && (
        <Alert variant="success" role="status" data-testid="externo-aprovado">
          <strong>{rotuloDe(externo.aprovadoId)}</strong> — {t('triagem.externo.aprovado')}
          {/* 🔴 QUEM APROVOU FICA NA TELA (relato do operador, 2026-08-18).
              O relato foi "aprovou só com o voto do RH". A trilha mostrava dois votos, e
              a réplica em teste confirmou que um voto não aprova — mas nada disso estava
              ao alcance de quem olhava a tela: o painel dizia "Aprovado pelo colegiado" e
              a lista de quem votou SUMIA justamente quando ela passa a importar mais.
              Foi preciso ler a trilha crua para responder "quem aprovou?", e essa não
              pode ser a única forma de saber. Dar acesso ao teor de uma denúncia é ato
              consequente e irreversível: quem o pratica fica nomeado. */}
          {externo.aprovaram.length > 0 && (
            <p className="externo-aprovadores" data-testid="externo-aprovadores">
              {t('triagem.externo.aprovado_por', { count: externo.aprovaram.length })}{' '}
              {externo.aprovaram.map((id) => rotuloDe(id)).join(', ')}
            </p>
          )}
        </Alert>
      )}

      {/* A queda da indicação anterior é MOSTRADA, não silenciada. Sem isto o nome
          simplesmente sumiria da tela e ninguém saberia se caiu, se foi retirado ou se é
          bug — o "fallback plausível esconde ausência" na sua versão de UI. */}
      {externo.aprovadoId === null && externo.ultimaReprovada !== null && (
        <Alert variant="warning" role="status" data-testid="externo-reprovado">
          <strong>{rotuloDe(externo.ultimaReprovada.externoId)}</strong> —{' '}
          {t('triagem.externo.reprovada')}
        </Alert>
      )}

      {externo.indicadoId !== null && externo.aprovadoId === null && (
        <div data-testid="externo-indicacao-aberta">
          <p>
            <strong>{rotuloDe(externo.indicadoId)}</strong>
          </p>
          <p>{t('triagem.externo.aguardando', { count: externo.faltam.length })}</p>
          {externo.faltam.length > 0 && (
            <p className="externo-faltam" data-testid="externo-faltam">
              {externo.faltam.map((id) => rotuloDe(id)).join(', ')}
            </p>
          )}

          {externo.euDecido && !jaVotei ? (
            <>
              <p>{t('triagem.externo.pergunta')}</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  size="sm"
                  loading={enviando === 'sim'}
                  disabled={enviando !== null}
                  onClick={() => void votar(true)}
                  data-testid="externo-aprovar"
                >
                  {t('triagem.externo.aprovar')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={enviando === 'nao'}
                  disabled={enviando !== null}
                  onClick={() => void votar(false)}
                  data-testid="externo-reprovar"
                >
                  {t('triagem.externo.reprovar')}
                </Button>
              </div>
            </>
          ) : jaVotei ? (
            /* VOTEI — a lista de quem falta continua acima (`externo-faltam`), então esta
               linha só precisa dizer que a minha parte está feita. */
            <p data-testid="externo-ja-votou">{t('triagem.externo.ja_votou')}</p>
          ) : (
            /* NÃO DECIDO — copy própria, e não a de "seu voto já foi registrado", que
               afirmava um voto que esta pessoa nunca deu. Cai aqui também o próprio
               indicado, que não vota na própria entrada. */
            <p data-testid="externo-nao-decide">{t('triagem.externo.nao_decide')}</p>
          )}
        </div>
      )}

      {erro !== null && (
        <Alert variant="error" role="alert" data-testid="externo-erro">
          {t(`triagem.externo.erro_${erro}`, {
            defaultValue: t('triagem.externo.erro_generico'),
          })}
        </Alert>
      )}
    </Card>
  );
}
